import type { ActionButton } from '@/components/actionsPopup/types';

/**
 * Button to view job applicants via router navigation.
 */
export function createViewApplicantsButton(jobId: string, overrides?: Partial<ActionButton>): ActionButton {
  return {
    id: 'applicants-job',
    value: 'View Applicants',
    to: { name: 'applications', params: { job_uuid: jobId } },
    variant: 'outlined',
    ...overrides,
  };
}

/**
 * Button to navigate to the job editing page.
 */
export function createEditJobButton(jobId: string): ActionButton {
  return {
    id: 'edit-job',
    value: 'Edit job',
    href: `/jobs/action_with_uuid?job_action=edit&uuid=${jobId}`,
    variant: 'outlined',
  };
}

/**
 * Button to initiate job closing (opens the close confirmation popup).
 */
export function createCloseJobButton(onClick: () => void): ActionButton {
  return {
    id: 'close-job',
    value: 'Close job',
    variant: 'outlined',
    onClick,
  };
}

/**
 * Button to share a job (Family Concierge feature).
 * Defaults to Vue router navigation (`to`); pass `useHref: true` for a full page navigation (`href`).
 */
export function createShareJobButton(jobId: string): ActionButton {
  const path = `/share-jobs?job_id=${jobId}`;
  return {
    id: 'share-job',
    value: 'Share Job',
    to: path,
    variant: 'outlined',
  };
}

/**
 * Button to re-open a previously closed job.
 */
export function createReopenJobButton(jobId: string): ActionButton {
  return {
    id: 'reopen-job',
    value: 'Re-open job',
    href: `/jobs/action_with_uuid?job_action=reopen&uuid=${jobId}`,
    variant: 'primary',
  };
}

/**
 * Confirm / Cancel button pair for the "Close this job?" confirmation popup.
 */
export function createCloseConfirmationButtons(callbacks: { onConfirm: () => void; onCancel: () => void }): ActionButton[] {
  return [
    { id: 'close-job-confirm', value: 'Close job', variant: 'primary', onClick: callbacks.onConfirm },
    { id: 'close-job-cancel', value: 'Cancel', variant: 'outlined', onClick: callbacks.onCancel },
  ];
}

/**
 * Buttons for managing an active/open job: edit, close, and optionally share (for Family Concierge accounts).
 * The share button is prepended when `isFamilyConcierge` is true.
 */
export function createActiveJobManagementButtons(options: {
  jobId: string;
  onCloseJob: () => void;
  isFamilyConcierge?: boolean;
}): ActionButton[] {
  const buttons: ActionButton[] = [createEditJobButton(options.jobId), createCloseJobButton(options.onCloseJob)];

  if (options.isFamilyConcierge) {
    buttons.unshift(createShareJobButton(options.jobId));
  }

  return buttons;
}
